<template>
  <q-dialog
    v-model="shown"
    :persistent="loading"
    :position="$q.screen.lt.sm ? 'right' : undefined"
    :class="$q.screen.lt.sm ? 'bg-white' : undefined"
    :maximized="$q.screen.lt.sm"
    data-test-id="order-options-dialog"
  >
    <q-card :style="{ 'width': $q.screen.lt.sm ? undefined : '550px', 'max-width': $q.screen.lt.sm ? undefined : '95%' }">
      <q-card-section
        class="row"
        dense
      >
        <template v-if="$q.screen.lt.sm">
          <q-btn
            v-close-popup
            icon="chevron_left"
            flat
            round
            dense
          />
        </template>
        <div class="text-h6">
          {{ $t('checkout.delivery.selectOptionPrompt') }}
        </div>
        <template v-if="$q.screen.gt.xs">
          <q-space />
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </template>
      </q-card-section>
      <q-card-section
        class="column"
      >
        <template v-if="showTiming">
          <div v-if="$register">
            <div class="text-h5">
              Due Time
            </div>
          </div>
          <q-card-section
            class="justify-center"
            :class="$register ? 'q-mb-sm row q-gutter-md' : 'q-pa-none'"
          >
            <q-select
              :model-value="optionValue"
              :label="$t('checkout.delivery.pickupAt')"
              class="col text-uppercase full-width"
              filled
              :options="dueTimeOptions"
              emit-value
              @update:model-value="value => setDueTimeOptionAndRelativeDueTime(value)"
            >
              <template #prepend>
                <q-icon
                  name="access_time"
                />
              </template>
              <template
                v-if="order.hasDueTime"
                #selected
              >
                <q-chip
                  color="primary"
                  :text-color="buttonTextColor"
                  icon="alarm"
                  style="margin-left: -10px; font-weight: bolder;"
                >
                  {{ optionValue }}
                </q-chip>
              </template>
            </q-select>
          </q-card-section>
        </template>

        <div
          class="q-mt-lg q-mb-sm row justify-between"
        >
          <div
            v-if="$register"
            class="text-h5"
          >
            Delivery Type
          </div>
          <q-space />
          <q-btn
            v-if="$register"
            icon="tune"
            @click="showDeliveryDetails = !showDeliveryDetails"
          />
        </div>
        <div
          v-if="$register || ($consumer && order.deliveryTypeOptions.length && order.deliveryTypeOptions.length > 1) || $kiosk"
          class="row justify-center"
        >
          <q-btn-toggle
            v-if="!order.deliveryTypeOptions.length"
            :model-value="false"
            class="col-auto"
            :options="[{ label: 'N/A', value: true }]"
            toggle-color="primary"
            :toggle-text-color="buttonTextColor"
            color="white"
            text-color="primary"
            rounded
            unelevated
            no-caps
            disable
          />
          <q-btn-toggle
            v-else-if="order.deliveryTypeOptions.length && order.deliveryTypeOptions.length < 2"
            v-model="order.deliveryType"
            class="full-width"
            :options="$_.map(order.deliveryTypeOptions, deliveryTypeOption => ({ label: deliveryTypeOption.label, value: deliveryTypeOption.value }))"
            toggle-color="primary"
            :toggle-text-color="$q.dark.isActive ? 'white' : 'black'"
            color="grey-7"
            text-color="primary"
            :size="$register || order.deliveryTypeOptions.length > 1 ? 'md' : 'lg'"
            outline
            unelevated
            no-caps
            spread
            :clearable="order.deliveryType !== order.defaultDeliveryType"
            @clear="order.deliveryType = order.defaultDeliveryType"
          />
          <q-select
            v-else
            v-model="order.deliveryType"
            :label="$t('checkout.delivery.deliveryTo')"
            class="text-uppercase full-width"
            filled
            :disable="disableDeliveryLocationOptions"
            :stack-label="false"
            emit-value
            map-options
            :options="$_.map(order.deliveryTypeOptions, deliveryTypeOption => ({ label: deliveryTypeOption.label, value: deliveryTypeOption.value }))"
          >
            <template
              #selected-item="scope"
            >
              <q-chip
                v-if="scope.opt.label "
                :tabindex="scope.tabindex"
                color="primary"
                :text-color="buttonTextColor"
              >
                {{ scope.opt.label }}
              </q-chip>
            </template>
          </q-select>
        </div>
        <div
          v-if="showDeliveryDetails"
          class="q-my-sm row q-gutter-sm"
        >
          <q-checkbox
            v-model="order.deliverReadyToServeItems"
            class="col"
            size="lg"
            label="Deliver Ready To Serve Items"
          />
          <q-checkbox
            v-model="order.deliverFastPrepItems"
            class="col"
            size="lg"
            label="Deliver Fast Prep Items"
          />
        </div>
        <div
          v-if="$_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, order.deliveryType)"
          class="q-gutter-md justify-center q-mt-xs q-mb-md"
          :class="$q.screen.gt.xs ? 'row' : 'column'"
        >
          <q-select
            :model-value="order.deliveryLocation"
            class="col"
            style="font-size: 24px;"
            :label="order.deliveryLocationLabel"
            :options="$_.map(deliveryLocationOptions, 'name')"
            :use-input="!order.deliveryLocation"
            input-debounce="50"
            :disable="disableDeliveryLocationOptions"
            clearable
            size="xl"
            @update:model-value="selectDeliveryLocation"
            @filter="filterDeliveryLocationOptions"
          >
            <template #selected-item="scope">
              <q-chip
                :tabindex="scope.tabindex"
                color="primary"
                :text-color="buttonTextColor"
              >
                {{ scope.opt }}
              </q-chip>
            </template>
          </q-select>
          <q-select
            :model-value="order.deliveryPositions"
            class="col"
            style="font-size: 24px;"
            :label="order.deliveryPositionLabel"
            :options="deliveryPositionOptions"
            :use-input="!order.deliveryPositions"
            input-debounce="50"
            :disable="disableDeliveryLocationOptions"
            multiple
            size="xl"
            @add="item => order.addDeliveryPosition(item.value)"
            @remove="item => order.removeDeliveryPosition(item.index)"
            @filter="filterDeliveryPositionOptions"
          >
            <template #selected-item="scope">
              <q-chip
                removable
                :tabindex="scope.tabindex"
                :label="scope.opt"
                color="primary"
                :text-color="buttonTextColor"
                @remove="scope.removeAtIndex(scope.index)"
              />
            </template>
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
                v-on="scope.itemEvents"
              >
                <q-item-section>
                  <q-item-label class="text-capitalize">
                    {{ scope.opt }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>
        <div
          v-if="order.customDeliveryType && order.customDeliveryType.hasDetails"
          class="row q-gutter-md justify-center q-mt-xs q-mb-md"
        >
          <q-input
            v-model="order.deliveryDetails"
            class="col-12"
            style="font-size: 24px; min-width: 310px;"
            :label="order.customDeliveryType.detailsFieldName"
            :hint="order.customDeliveryType.detailsMessage"
            size="xl"
          />
        </div>
        <template v-if="order.deliveryType !== DELIVERY_TYPE_OPTIONS.DIRECT_TO_CUSTOMER && (order.collectNameForOrder || order.collectPagerNumber)">
          <div class="row q-gutter-md q-mt-md">
            <div
              class="q-mb-sm row justify-between"
            >
              <div class="text-h5">
                Pickup Identifier
              </div>
            </div>
          </div>
          <div
            v-if="order.collectNameForOrder"
            class="q-mb-md row items-center q-gutter-md"
          >
            <q-chip
              v-if="user && !user.loading"
              class="col text-bold"
              :color="user.membership && user.membership.state =='active' && !user.membershipType.free ? 'primary' : 'grey-7'"
              size="xl"
              :text-color="user.membership && user.membership.state =='active' && !user.membershipType.free ? buttonTextColor : 'white'"
              removable
              :label="user.name"
              @remove="order.removeUser()"
            />
            <q-input
              v-if="!user"
              v-model="order.nameForOrder"
              class="col q-mt-md"
              placeholder="Name For Order"
              :stack-label="false"
              input-style="font-size: 24px;"
              data-test-id="order-options-dialog-name-for-order-input"
            />
            <q-btn
              v-if="!user && $register"
              color="primary"
              size="lg"
              class="col"
              outline
              @click="$emit('find-user')"
            >
              {{ $t('user.actions.find') }}
            </q-btn>
          </div>
          <div
            v-if="order.collectNameForOrder && order.collectPagerNumber && !(order.user || order.nameForOrder)"
            class="q-mb-md row justify-center items-center"
          >
            — AND / OR —
          </div>
          <div
            v-else-if="order.collectNameForOrder && order.collectPagerNumber"
            class="q-mb-md row justify-center items-center"
          >
            — AND —
          </div>
          <div
            v-if="order.collectPagerNumber"
            class="q-mb-md row items-center q-gutter-md"
          >
            <q-input
              v-model="order.pagerNumber"
              class="col q-mt-md"
              :label="order.pagerNumber ? 'Pager Number' : undefined"
              placeholder="Pager Number"
              :stack-label="!!order.pagerNumber"
              input-style="font-size: 24px;"
            />
          </div>
        </template>
        <template v-if="$register && order.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE">
          <div>
            <div class="text-h5 q-mt-md">
              Number of Guests
            </div>
          </div>
          <q-card-section class="row q-gutter-md justify-center">
            <q-input
              v-model.number="order.numberOfGuests"
              style="font-size: 24px; min-width: 310px;"
              type="number"
              step="1"
              min="0"
              input-class="text-center"
              size="lg"
              @update:model-value="value => { if (value === '') order.numberOfGuests = null }"
            />
          </q-card-section>
        </template>
        <template v-if="$register && order.nameForOrder">
          <div>
            <div class="text-h5 q-mt-md">
              Name For Order
            </div>
          </div>
          <q-card-section class="row q-gutter-md justify-center">
            <q-input
              v-model="order.nameForOrder"
              style="font-size: 24px; min-width: 310px;"
              input-class="text-center"
              size="lg"
              :outlined="false"
            />
          </q-card-section>
        </template>
        <template v-if="$register && order.canTransferTab">
          <div class="text-h5 q-mt-md">
            Transfer Tab
          </div>
          <q-card-section class="row q-gutter-md justify-center">
            <q-btn
              label="Select User"
              color="primary"
              size="lg"
              outline
              @click="$emit('show-incoming-employee')"
            />
          </q-card-section>
        </template>
      </q-card-section>

      <q-card-section class="row items-center q-mt-lg">
        <q-btn
          :size="$q.screen.lt.md ? 'lg' : 'xl'"
          class="payment-button col"
          :disable="!order.deliveryInformationProvided"
          :color="order.deliveryInformationProvided ? 'primary' : 'grey'"
          :text-color="buttonTextColor"
          data-test-id="order-options-dialog-submit"
          @click="done = true; shown = false; $emit('next')"
        >
          {{ nextStepLabel }}
        </q-btn>
      </q-card-section>
    </q-card>
    <q-dialog v-model="showDueTimeDialog">
      <q-card :style="{ 'width': '750px', 'max-width': '95%' }">
        <q-card-section
          class="q-gutter-md row items-start"
          :class="$q.screen.lt.sm ? 'column' : undefined"
        >
          <q-date
            class="col"
            :model-value="order.dueTime && date.formatDate(order.dueTime, 'YYYY-MM-DDTHH:mm:ssZ')"
            flat
            mask="YYYY-MM-DDTHH:mm:ssZ"
            :options="validDateOption"
            @update:model-value="value => order.dueTime = new Date(value)"
          />
          <q-time
            class="col"
            :style="{ 'width': '750px', 'max-width': '95%' }"
            :model-value="order.dueTime && date.formatDate(order.dueTime, 'YYYY-MM-DDTHH:mm:ssZ')"
            flat
            mask="YYYY-MM-DDTHH:mm:ssZ"
            :options="validTimeOption"
            @update:model-value="value => order.dueTime = new Date(date.formatDate(order.dueTime, 'YYYY-MM-DD') + date.formatDate(value, 'THH:mm:ss') + date.formatDate(order.dueTime, 'Z'))"
          />
        </q-card-section>
        <q-card-actions class="row justify-end q-ma-sm">
          <q-btn
            label="Done"
            color="primary"
            :text-color="buttonTextColor"
            :loading="loading"
            @click="showDueTimeDialog = false"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <action-dialog
      ref="openTabActionDialog"
      title="Found Open Tab For Table"
      :title-chip="foundOrder ? 'Tab Id ' + foundOrder.id : null"
      :message="foundOrderDialogMessage"
      ok-label="Continue New Order"
      cancel-label="Use Open Tab"
      @ok="continueOrder"
      @cancel="mergeOrders"
    />
  </q-dialog>
</template>

<script>
import { date } from 'quasar'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import { DUE_TIME_MINUTES_FROM_SHOWING_START } from 'src/models/showing/Showing'
import ActionDialog from 'src/modules/consumer/components/ActionDialog'

const dateThreshold = date.formatDate(new Date(), 'YYYY/MM/DD')

export default {
  name: 'OrderOptionsDialog',
  context: ['currentRegisterClient'],
  configuration: ['currentSite', 'deliveryLocations'],
  components: {
    ActionDialog
  },
  models: {
    orders: {
      variables () {
        return {
          state: 'open_tab',
          deliveryLocation: this.deliveryLocation,
          orderToExclude: this.order.isRemote ? this.order.id : null,
          limit: 1,
          skip: true
        }
      },
      immediate: false
    }
  },
  props: {
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    },
    order: {
      type: Object,
      required: true
    },
    showTiming: {
      type: Boolean,
      required: false,
      default: false
    },
    nextStepLabel: {
      type: String,
      required: false,
      default: 'Done'
    }
  },
  emits: ['find-user', 'show-incoming-employee', 'next', 'update:model-value', 'dismiss', 'merge-tab'],
  data () {
    return {
      done: false,
      shown: false,
      deliveryLocationOptions: [],
      deliveryPositionOptions: [],
      showDueTimeDialog: false,
      showDeliveryDetails: false,
      deliveryLocation: null,
      loading: false,
      date
    }
  },
  computed: {
    user () {
      return this.order?.user
    },
    disableDeliveryLocationOptions () {
      // When we have a delivery position set via url paramters, we should not allow the user to change the delivery location
      // This is a temporary solution until we have a better way to handle this
      // In the future we will be more explicit about these types of restrictions
      return this.$consumer && !_.isNil(this.$route.query['delivery-position'])
    },
    foundOrder () {
      return this.orders?.data && this.orders.data[0]
    },
    foundOrderDialogMessage () {
      const foundOrderName = this.foundOrder?.nameForOrder || this.foundOrder?.name ? `"${this.foundOrder?.nameForOrder || this.foundOrder?.name}" ` : ''
      const foundOrderLocation = this.foundOrder?.deliveryLocation ? `location ${this.foundOrder?.deliveryLocation}` : 'the selected location'

      return `Tab ${foundOrderName}is already open for ${foundOrderLocation}. Create a new order or use the existing open tab.`
    },
    optionValue () {
      if (this.order.hasDueTime) {
        return this.order.formattedDueTime
      } else {
        return 'Now'
      }
    },
    dueTimeOptions () {
      let options = []

      if (this.$register || !this.order.nextShowing || this.order.nextShowing.siteTime < this.currentSite.siteTime) {
        options.push({ value: false, label: 'Now' })
      }

      const showings = _.map(this.order.futureShowings, showing => {
        return { value: showing, label: showing.descriptionWithDefaultDueTime }
      })
      options = [...options, ...showings]

      if (this.$register) {
        options.push({ value: true, label: 'Choose...' })
      }

      return options
    },
    buttonTextColor () {
      return this.$isColorDark(this.currentSite.primaryColor) ? 'white' : 'black'
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
      this.$emit('dismiss', !this.shown && !this.done)

      if (!this.shown) return

      this.mergeOrderItemDeliveryDetails()

      if (!this.order.numberOfGuests) {
        let firstShowingId = null
        const estimatedNumberOfGuests = _.filter(this.order.orderItems.data, orderItem => {
          if (!orderItem.ticket) return false

          firstShowingId ||= orderItem.ticket.showing.id
          return firstShowingId === orderItem.ticket.showing.id
        }).length
        if (estimatedNumberOfGuests > 0) {
          this.order.numberOfGuests = estimatedNumberOfGuests
        }
      }
    },
    'order.deliveryType' () {
      _.each(this.order.orderItems.data, orderItem => {
        if (!orderItem.deliveryType) {
          if (!orderItem.deliveryLocation) {
            const deliveryLocation = _.find(this.deliveryLocations.data, deliveryLocation => {
              return deliveryLocation.name === this.order.deliveryLocation
            })

            if (orderItem.deliveryPositions?.length && (_.without(orderItem.deliveryPositions, ...deliveryLocation.positions)).length !== 0) {
              orderItem.resetDeliveryPositions()
            }
            return
          }

          const deliveryLocation = _.find(this.deliveryLocations.data, deliveryLocation => {
            return deliveryLocation.name === orderItem.deliveryLocation
          })

          if (deliveryLocation?.type !== this.order.deliveryType) {
            orderItem.deliveryLocation = null
            orderItem.resetDeliveryPositions()
          }
        }
      })
    },
    'order.deliveryLocation' () {
      _.each(this.order.orderItems.data, orderItem => {
        if (!orderItem.deliveryLocation) {
          const deliveryLocation = _.find(this.deliveryLocations.data, deliveryLocation => {
            return deliveryLocation.name === this.order.deliveryLocation
          })

          if (orderItem.deliveryPositions.length && (_.without(orderItem.deliveryPositions, ...deliveryLocation.positions)).length !== 0) {
            orderItem.resetDeliveryPositions()
          }
        }
      })
    }
  },
  created () {
    _.extend(this, { DELIVERY_TYPE_OPTIONS })
  },
  methods: {
    show () {
      this.shown = true
      this.done = false
    },
    setDueTimeOptionAndRelativeDueTime (option) {
      if (option?.typeName === 'Showing') {
        this.order.dueTime = date.addToDate(option.time, { minutes: DUE_TIME_MINUTES_FROM_SHOWING_START })
        this.order.hasDueTime = true
      } else if (option === false) {
        this.order.hasDueTime = false
      } else {
        this.order.hasDueTime = true
        this.showDueTimeDialog = true
      }
    },
    mergeOrderItemDeliveryDetails () {
      const orderItemsWithMatchingDeliveryType = _.filter(this.order.orderItems.data, orderItem => {
        if (orderItem.type !== ORDER_ITEM_TYPES.ITEM) return

        // Look for any order items with a delivery type that matches the order's delivery type
        const orderItemDeliveryType = orderItem.deliveryType || orderItem.defaultDeliveryType
        if (this.order.deliveryType !== orderItemDeliveryType) return

        // Look for any order items with a delivery location that matches the order's delivery location
        const orderItemDeliveryLocation = orderItem.deliveryLocation || orderItem.defaultDeliveryLocation
        if (!orderItemDeliveryLocation) return

        // If the order has a delivery location, only match order items with the same delivery location
        if (this.order.deliveryLocation && orderItemDeliveryLocation !== this.order.deliveryLocation) return

        return true
      })

      if (orderItemsWithMatchingDeliveryType.length) {
        // If the order has a delivery location, don't change it
        this.order.deliveryLocation ||= orderItemsWithMatchingDeliveryType[0].deliveryLocation || orderItemsWithMatchingDeliveryType[0].defaultDeliveryLocation
        // Find all unique delivery positions from order items with matching delivery type and location
        const itemsWithMatchingDeliveryLocation = _.filter(orderItemsWithMatchingDeliveryType, orderItem => {
          const orderItemDeliveryLocation = orderItem.deliveryLocation || orderItem.defaultDeliveryLocation
          return orderItemDeliveryLocation === this.order.deliveryLocation
        })
        const uniqueLocations = _(itemsWithMatchingDeliveryLocation).map('deliveryPositions').flatten().compact().uniq().value()

        // Update the order's delivery positions to include all unique delivery positions
        this.order.deliveryPositions = _.uniq([...uniqueLocations, ...this.order.deliveryPositions])
      }
    },
    filterDeliveryLocationOptions (value, update) {
      if (this.order.deliveryLocationOptions) {
        if (value === '') {
          update(() => {
            this.deliveryLocationOptions = this.order.deliveryLocationOptions
          })
        } else {
          update(() => {
            this.deliveryLocationOptions = _.filter(this.order.deliveryLocationOptions, deliveryLocation => {
              return deliveryLocation.name.includes(value)
            })
          })
        }
      }
    },
    filterDeliveryPositionOptions (value, update) {
      if (this.order.deliveryPositionOptions) {
        if (value === '') {
          update(() => {
            this.deliveryPositionOptions = this.order.deliveryPositionOptions
          })
        } else {
          update(() => {
            this.deliveryPositionOptions = _.filter(this.order.deliveryPositionOptions, deliveryPosition => {
              return deliveryPosition.includes(value)
            })
          })
        }
      }
    },
    validDateOption (date) {
      return date >= dateThreshold
    },
    validTimeOption (hour, minute) {
      if (date.formatDate(this.order.dueTime, 'YYYY/MM/DD') > dateThreshold) {
        return true
      }

      const minutes = hour * 60 + minute
      const now = new Date()
      const nowMinutes = now.getHours() * 60 + now.getMinutes()

      return minutes > nowMinutes + 10
    },
    async selectDeliveryLocation (deliveryLocation) {
      this.deliveryLocation = deliveryLocation

      if (deliveryLocation === null || !_.includes(['draft', 'open_tab'], this.order.state) || this.order.deliveryType !== DELIVERY_TYPE_OPTIONS.TABLE || !this.$register) {
        this.order.deliveryLocation = deliveryLocation
        return
      }

      this.loading = true

      await this.$nextTick()
      this.orders.options.variables.deliveryLocation = deliveryLocation
      await this.orders.fetch(true)

      if (this.orders.data.length > 0) {
        this.$refs.openTabActionDialog.show()
      } else {
        this.order.deliveryLocation = deliveryLocation
      }

      this.loading = false
    },
    continueOrder () {
      this.order.deliveryLocation = this.deliveryLocation
    },
    mergeOrders () {
      this.order.deliveryLocation = this.deliveryLocation
      this.order.nameForOrder ||= this.foundOrder.nameForOrder
      this.$emit('merge-tab', this.foundOrder)
    }
  }
}

</script>
